import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import {
  useStore,
  useStoreGetters,
  useMapGetter,
} from 'dashboard/composables/store';
import EventsAPI from 'dashboard/api/planning/events';

import {
  getActionOptions,
  getConditionOptions,
} from 'dashboard/helper/automationHelper';
import {
  MESSAGE_CONDITION_VALUES,
  MESSAGE_IMAGE_TYPE_VALUES,
  PRIORITY_CONDITION_VALUES,
  EVENT_SOURCE_CONDITION_VALUES,
} from 'dashboard/constants/automation';

/**
 * This is a shared composables that holds utilities used to build dropdown and file options
 * @returns {Object} An object containing various automation-related functions and computed properties.
 */
export default function useAutomationValues() {
  const store = useStore();
  const getters = useStoreGetters();
  const { t } = useI18n();
  const agents = useMapGetter('agents/getAgents');
  const campaigns = useMapGetter('campaigns/getAllCampaigns');
  const contacts = useMapGetter('contacts/getContacts');
  const inboxes = useMapGetter('inboxes/getInboxes');
  const labels = useMapGetter('labels/getLabels');
  const eventLabels = useMapGetter('planningEventLabels/getRecords');
  const teams = useMapGetter('teams/getTeams');
  const slaPolicies = useMapGetter('sla/getSLA');
  const bookingWidgets = useMapGetter('bookingWidgets/getBookingWidgets');
  const eventSystemStageOptions = ref([]);
  const eventCustomStatusOptions = ref([]);
  const planningEventStatusOptions = computed(() => [
    ...(eventSystemStageOptions.value || []),
    ...(eventCustomStatusOptions.value || []),
  ]);
  const loadPlanningEventStatusOptions = async () => {
    try {
      const response = await EventsAPI.statusOptions();
      eventSystemStageOptions.value = (response.data.system_statuses || []).map(
        status => ({
          id: status.system_stage,
          name: status.label,
        })
      );
      eventCustomStatusOptions.value = (
        response.data.custom_statuses || []
      ).map(status => ({
        id: status.key,
        name: status.label,
      }));
    } catch {
      eventSystemStageOptions.value = [];
      eventCustomStatusOptions.value = [];
    }
  };

  onMounted(() => {
    loadPlanningEventStatusOptions();
    if (!bookingWidgets.value?.length) {
      store.dispatch('bookingWidgets/get').catch(() => {});
    }
  });

  const booleanFilterOptions = computed(() => [
    { id: true, name: t('FILTER.ATTRIBUTE_LABELS.TRUE') },
    { id: false, name: t('FILTER.ATTRIBUTE_LABELS.FALSE') },
  ]);

  const statusFilterItems = computed(() => {
    return {
      open: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.open.TEXT'),
      },
      resolved: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.TEXT'),
      },
      pending: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.pending.TEXT'),
      },
      snoozed: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.snoozed.TEXT'),
      },
      all: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.all.TEXT'),
      },
    };
  });

  const statusFilterOptions = computed(() => {
    const statusFilters = statusFilterItems.value;
    return [
      ...Object.keys(statusFilters).map(status => ({
        id: status,
        name: statusFilters[status].TEXT,
      })),
      { id: 'all', name: t('CHAT_LIST.FILTER_ALL') },
    ];
  });

  const messageTypeOptions = computed(() =>
    MESSAGE_CONDITION_VALUES.map(item => ({
      id: item.id,
      name: t(`AUTOMATION.MESSAGE_TYPES.${item.i18nKey}`),
    }))
  );

  const messageImageTypeOptions = computed(() =>
    MESSAGE_IMAGE_TYPE_VALUES.map(item => ({
      id: item.id,
      name: t(`AUTOMATION.MESSAGE_IMAGE_TYPES.${item.i18nKey}`),
    }))
  );

  const priorityOptions = computed(() =>
    PRIORITY_CONDITION_VALUES.map(item => ({
      id: item.id,
      name: t(`AUTOMATION.PRIORITY_TYPES.${item.i18nKey}`),
    }))
  );

  const eventSourceOptions = computed(() =>
    EVENT_SOURCE_CONDITION_VALUES.map(item => ({
      id: item.id,
      name: t(`AUTOMATION.EVENT_SOURCE_TYPES.${item.i18nKey}`),
    }))
  );

  const eventLabelNoneOption = computed(() => ({
    id: 'null',
    name: t('EVENT_LABEL_MGMT.DROPDOWN.NO_LABEL'),
  }));

  /**
   * Adds a translated "None" option to the beginning of a list
   * @param {Array} list - The list to add "None" to
   * @returns {Array} A new array with "None" option at the beginning
   */
  const addNoneToList = list => [
    {
      id: 'nil',
      name: t('AUTOMATION.NONE_OPTION') || 'None',
    },
    ...(list || []),
  ];

  /**
   * Gets the condition dropdown values for a given type.
   * @param {string} type - The type of condition.
   * @returns {Array} An array of condition dropdown values.
   */
  const getConditionDropdownValues = type => {
    return getConditionOptions({
      agents: agents.value,
      booleanFilterOptions: booleanFilterOptions.value,
      campaigns: campaigns.value,
      contacts: contacts.value,
      customAttributes: getters['attributes/getAttributes'].value,
      inboxes: inboxes.value,
      labels: labels.value,
      statusFilterOptions: statusFilterOptions.value,
      priorityOptions: priorityOptions.value,
      messageTypeOptions: messageTypeOptions.value,
      messageImageTypeOptions: messageImageTypeOptions.value,
      planningEventStatusOptions: planningEventStatusOptions.value,
      eventSourceOptions: eventSourceOptions.value,
      bookingWidgets: bookingWidgets.value,
      teams: teams.value,
      languages,
      countries,
      type,
      eventLabels: eventLabels.value,
      eventLabelNoneOption: eventLabelNoneOption.value,
    });
  };

  /**
   * Gets the action dropdown values for a given type.
   * @param {string} type - The type of action.
   * @returns {Array} An array of action dropdown values.
   */
  const getActionDropdownValues = type => {
    return getActionOptions({
      agents: agents.value,
      labels: labels.value,
      eventLabels: eventLabels.value,
      teams: teams.value,
      slaPolicies: slaPolicies.value,
      languages,
      type,
      addNoneToListFn: addNoneToList,
      priorityOptions: priorityOptions.value,
    });
  };

  return {
    booleanFilterOptions,
    statusFilterItems,
    statusFilterOptions,
    priorityOptions,
    messageTypeOptions,
    eventSystemStageOptions,
    eventCustomStatusOptions,
    planningEventStatusOptions,
    getConditionDropdownValues,
    getActionDropdownValues,
    agents,
    campaigns,
    contacts,
    inboxes,
    labels,
    eventLabels,
    teams,
    slaPolicies,
  };
}
